<script lang="ts">
    // Svelte/Jet/Models/Types
    import { createEventDispatcher, onMount } from 'svelte';
    import { getJetPerform } from '~/jet';
    import type { QueueItemMetadata } from '~/jet/models';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { mediaQueries } from '@amp/web-app-components/src/stores/media-query';
    import { selectedItemIndex } from '~/stores/selected-item-index';

    // Utils
    import { getRafQueue } from '@amp/web-app-components/src/utils/rafQueue';
    import allowDrop, {
        DropTarget,
    } from '@amp/web-app-components/src/actions/allow-drop';
    import { getLocale } from '~/utils/locale';

    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import LineClamp from '@amp/web-app-components/src/components/LineClamp/LineClamp.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';

    const i18n = getI18n();
    const locale = getLocale();
    const perform = getJetPerform();
    const dispatch = createEventDispatcher();
    const rafQueue = getRafQueue();

    let listItemElement: HTMLElement;
    // Updates contextual menu config to render the <amp-contextual-menu />
    // element somewhere other than the default of document.body
    let parentElement: HTMLElement;

    let multilineTitle = true; // Default to 2 lines title and 1 line subtitle

    export let item: QueueItemMetadata;
    export let dropEnabled: boolean = false;
    export let isPanel: boolean = false;
    export let index: number | null = null;

    $: artwork = item.artwork;
    $: getArtworkProfile = () => {
        return 'track-list';
    };
    $: title = item.movementName;
    $: artistName = item.artistName;
    $: playAction = item.playAction;

    $: isMobile = $mediaQueries === 'xsmall' || $mediaQueries === 'small';
    $: isSelected = $selectedItemIndex === index;

    // Moves focus to this list item, but only if focus is not already inside
    // the item (so we don't steal focus from More button, if it was tabbed to).
    $: if (isSelected) {
        setFocus();
    }

    // Schedule multiline updates with RAF queue
    const updateMultilineTitle = (e: CustomEvent) =>
        rafQueue.add(() => (multilineTitle = e.detail.multiline));

    const removeFromQueue = () => {
        perform(item.removeFromQueueAction);
    };

    // Used to focus the item on click or arrow navigation.
    const focusItem = () => {
        listItemElement?.focus();
    };

    // Used to remove focus on keyboard Enter/double-click
    // on desktiop -or- on mobile single-click
    const removeFocus = () => {
        selectedItemIndex.set(null);

        if (!isMobile) {
            if (isPanel) {
                // Blur before focusing on a new item to ensure Safari blurs
                // the focused element before moving focus elsewhere
                listItemElement?.blur();
                document.querySelector<HTMLElement>('.side-panel')?.focus();
            } else {
                // TODO: rdar://150774509 ([Classical on Web] Queue selection state in Desktop Now Playing View)
                //       There aren't really tab states in Desktop Now Playing, or if there are, they aren't
                //       visible.  Update this selector once we work on the above radar to something selectable.
                document
                    .querySelector<HTMLElement>('.now-playing-queue')
                    ?.focus();
            }
        } else {
            listItemElement?.blur();
        }
    };

    const setFocus = () => {
        const itemWithFocus = document.activeElement.closest('.up-next-item');

        if (listItemElement !== itemWithFocus) {
            focusItem();
        }
    };

    // Used with focus handler is used to ensure that VoiceOver navigation
    // is in sync with FKA.
    const selectItem = () => {
        if (!isSelected) {
            selectedItemIndex.set(index);
            focusItem();
        } else if (isSelected) {
            // Ensures item is focused if reclicked.  Use case is when a user clicks
            // on the item in the sidepanel Queue, then clicks outside of that.  The
            // item is selected but not focused.  When clicking back on the item,
            // this will ensure that it regains it's focus.
            setFocus();
        }
    };

    function handlePlayKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            perform(playAction);
            removeFocus();
        }
    }

    const handleClick = () => {
        if (isMobile) {
            perform(playAction);
            removeFocus();
        }
    };

    // Double-clicking on an item plays the item.  Ensure that the
    // selected item index is reset and we blur the current focus
    // otherwise the item that takes this spot in the list
    // will be focused
    const handleDoubleClick = () => {
        perform(playAction);
        removeFocus();
    };

    onMount(() => {
        if (!isMobile && isPanel) {
            parentElement = document.querySelector('#modal-content-wrapper');
        }
    });
</script>

<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<!-- svelte-ignore a11y-no-redundant-roles -->
<li
    class="up-next-item"
    class:selected={isSelected}
    role="listitem"
    tabindex="0"
    aria-label={$i18n.t('ASE.Web.MusicClassical.VO.PlayLabel', {
        content: title,
    })}
    bind:this={listItemElement}
    on:dblclick|preventDefault|stopPropagation={handleDoubleClick}
    on:click={handleClick}
    on:focus|capture={selectItem}
    on:keydown|self={handlePlayKeydown}
    on:mousedown|preventDefault={selectItem}
    use:allowDrop={dropEnabled && {
        dropEnabled: true,
        targets: [DropTarget.Top, DropTarget.Bottom],
        onDrop: (dropData) => dispatch('drop', dropData),
    }}
    data-testid="up-next-item"
>
    <div class="artwork-wrapper">
        <Artwork {artwork} profile={'track-list'} forceCropCode={true} />
    </div>

    <div class="up-next-item-title">
        <LineClamp clamp={2} on:resize={updateMultilineTitle} observe={true}>
            <div class="title" data-testid="up-next-item-title">
                {title}
            </div>
        </LineClamp>
        <div
            class="subtitle"
            data-testid="up-next-item-subtitle"
            style:--subtitle-line-clamp={multilineTitle ? 1 : 2}
        >
            {artistName}
        </div>
    </div>

    <button
        class="up-next-item-remove"
        data-testid="up-next-item-remove"
        aria-label={$i18n.t('ASE.Web.MusicClassical.UpNext.RemoveItem')}
        on:click={removeFromQueue}
    />

    <div class="time-and-controls">
        <div class="controls" data-testid="up-next-item-controls">
            <ProductContextMenu
                contextMenuAction={item.contextMenuAction}
                hasMaterial={false}
                buttonStyle="non-platter"
                bind:parentElement
            />
        </div>
    </div>
</li>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/tabular-numbers' as *;
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    $track-side-padding: 10px;
    $track-column-gap: 12px;
    $track-height: 55px;
    $track-artwork-size: 40px;
    $track-time-size: auto;

    .up-next-item {
        --artwork-override-height: auto;
        --artwork-override-max-width: #{$track-artwork-size};
        --artwork-override-max-height: #{$track-artwork-size};
        --artwork-override-width: #{$track-artwork-size};
        display: grid;
        grid-template-columns: [left-edge] $track-artwork-size [artwork-edge] 1fr [title-edge] $track-time-size [right-edge];
        grid-template-rows: $track-height;
        align-items: center;
        border-radius: 6px;
        outline: none; // The focused item takes styling cues from the `selected` class
        padding-inline-start: $track-side-padding;
        padding-inline-end: $track-side-padding;

        &::before {
            content: '';
            grid-column: artwork-edge / right-edge;
            grid-row: 1 / -1;
            height: 0;
            align-self: start;
            border-top: var(
                --up-next-item-divider-style,
                var(--keyline-border-style)
            );
        }

        &:first-of-type {
            &::before {
                grid-column: left-edge / right-edge;
            }
        }

        &.selected {
            background-color: var(--systemQuaternary);

            &::before,
            + :global(.up-next-item)::before {
                border-top: none;
            }
        }

        &:focus {
            --selectedTextColor: white;
            --contextMenuEllipsisFillOverride: white;
            background-color: var(--selectionColor);
        }
    }

    .artwork-wrapper {
        position: relative;
        border-radius: $global-border-radius-xsmall;
        grid-column: left-edge / artwork-edge;
        grid-row: 1 / -1;
    }

    .up-next-item-remove {
        $removeButtonOffset: -11px;
        grid-column: 1;
        grid-row: 1;
        place-self: start;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background-image: url('/assets/icons/Remove.png');
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        opacity: 0;
        pointer-events: none;

        // Positions center of asset to top inline corner
        transform: translate(
            var(--removeButtonOffset, #{$removeButtonOffset}),
            -3px
        );
        z-index: calc(var(--z-default) + 1);

        // Flip offset in opposite direction in RTL
        @include rtl {
            --removeButtonOffset: #{-$removeButtonOffset};
        }

        // `:focus` is used here because the browser doesn't apply
        // `:focus-visible` styles when this element is focused with VO.
        .up-next-item:hover &,
        &:focus {
            opacity: 1;
            pointer-events: auto;
        }

        &:focus {
            @include focus-shadow-inset;
        }
    }

    .up-next-item-title {
        grid-column: artwork-edge / title-edge;
        grid-row: 1 / -1;
        margin-inline-start: $track-column-gap;
    }

    .title {
        color: var(--selectedTextColor, var(--systemPrimary));
    }

    .subtitle {
        margin-top: 2px;
        color: var(--selectedTextColor, var(--systemSecondary));
        font: var(--callout);

        @include line-clamp(var(--subtitle-line-clamp, 2));
    }

    .time-and-controls {
        --controlsOpacity: 0;
        --timeOpacity: calc(1 - var(--controlsOpacity));
        grid-column: title-edge / right-edge;
        grid-row: 1 / -1;
        display: grid;
        grid-template-areas: 'time-and-controls';
        align-items: center;
        justify-content: end;

        .up-next-item:hover &,
        &:focus-within {
            --controlsOpacity: 1;
        }
    }

    .controls {
        grid-area: time-and-controls;
        opacity: var(--controlsOpacity, 0);
        z-index: calc(var(--z-default) + 2);
    }
</style>
